import React, { useEffect, useState } from 'react';
import {
  Stack,
  Box,
  Card,
  TablePagination,
  TableRow,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  Collapse,
  Typography,
  Button,
} from '@mui/material';
import Channels from '../form-elements/channels.component';
import ChannelScheduleViewType from '../form-elements/channel-schedule-view-type.component';
import { getChannelVideoScheduleList } from '../../services/apps.service';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import Loading from '../table-elements/loading.component';
import NoData from '../table-elements/no-data.component';
import Sortable from '../table-elements/sortable.component';
import Error from '../table-elements/error.component';
import { getSundaysBetween } from '../../utils/datetime.util';
import { COLUMNS } from '../../config/module-configs/channel-preview.config';
import CellValue from '../table-elements/cell-value.component';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ChannelPreview = () => {
  const FILTERS_KEY = 'channel-preview';

  const { getSignal, isAbortError } = useAbortableRequest();

  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedView, setSelectedView] = useState('MOVIES');
  const [sundayDates, setSundayDates] = useState([]);
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    appliedFilters: {},
    loading: false,
    columns: COLUMNS,
  });

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      loading: true,
      reloadCounter: options.reloadCounter + 1,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      loading: true,
      reloadCounter: options.reloadCounter + 1,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      loading: true,
      reloadCounter: options.reloadCounter + 1,
    });
  };
  const handleViewChange = (e) => {
    setOptions({
      ...options,
      page: 0,
      sortBy: '',
      sortOrder: '',
      loading: true,
      reloadCounter: options.reloadCounter + 1,
    });
    setSelectedView(e.target.value);
  };

  const handleShowSubRow = (rowData) => {
    setOptions((prevOptions) => ({
      ...prevOptions,
      rows: prevOptions.rows.map((item) =>
        item.id === rowData.id
          ? { ...item, isExpanded: !item.isExpanded }
          : item
      ),
    }));
  };

  const handleShowSubSubRow = (row, subrow) => {
    setOptions((prevOptions) => ({
      ...prevOptions,
      rows: prevOptions.rows.map((r) =>
        r.id === row.id
          ? {
              ...r,
              subRows: r.subRows?.map((subRow) =>
                subRow.id === subrow.id
                  ? { ...subRow, isExpanded: !subRow.isExpanded }
                  : subRow
              ),
            }
          : r
      ),
    }));
  };

  const fetchVideoData = (channelId, view) => {
    const signal = getSignal();
    setOptions({ ...options, loading: true });

    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    params.push(`view=${view}`);

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getChannelVideoScheduleList(channelId, paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          const sundays = getSundaysBetween(null, null);
          let columns = COLUMNS;
          if (
            view === 'COLLECTIONS' ||
            view === 'SERIES' ||
            view === 'APP_VIEW'
          ) {
            columns = [
              {
                id: 'fields',
                label: '',
                width: '2%',
                align: 'center',
                dataKey: 'fields',
                sortable: false,
                type: CELL_DATA_TYPES.SUB_LIST,
              },
              ...COLUMNS,
            ];
          }
          setSundayDates(sundays);
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows:
              res?.data?.rows.map((item) => ({ ...item, isExpanded: false })) ||
              [],
            columns,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setOptions({
          ...options,
          loading: false,
          totalRows: 0,
          rows: [],
        });
      });
  };

  const getStickyColumnStyle = (colIndex, columnId) => {
    if (selectedView === 'MOVIES' && columnId === 'title') {
      return {
        position: 'sticky',
        left: 0,
        minWidth: '250px',
      };
    }

    if (selectedView !== 'MOVIES') {
      if (colIndex === 0) {
        return {
          position: 'sticky',
          left: 0,
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
        };
      }
      if (columnId === 'title') {
        return {
          position: 'sticky',
          left: 60,
          minWidth: '250px',
        };
      }
    }

    return {};
  };

  useEffect(() => {
    const localFilters = getLocalFilterValue(FILTERS_KEY);
    if (localFilters && Object.keys(localFilters).length > 0) {
      if (localFilters.channel) {
        setSelectedChannel(localFilters.channel);
      }
      setSelectedView(localFilters.view);
    }
    if (selectedChannel?.id || localFilters.channel) {
      fetchVideoData(
        selectedChannel?.id || localFilters.channel?.id,
        localFilters.view || selectedView
      );
    } else {
      setOptions({
        page: 0,
        rowsPerPage: 100,
        totalRows: 0,
        rows: [],
        reloadCounter: 0,
        appliedFilters: {},
        loading: false,
        columns: COLUMNS,
      });
    }
  }, [options.reloadCounter]);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 0, py: 2 }}
      >
        <Box display="flex" alignItems="center" width="50%" gap={1}>
          <Channels
            id="channels"
            name="channels"
            label="Select Channel"
            defaultValue={selectedChannel}
            onChange={(v) => {
              setLocalFilterValue(
                FILTERS_KEY,
                { channel: v, view: selectedView } || {}
              );
              setSelectedChannel(v);
              handleRefreshData();
            }}
            sx={{ width: '100%' }}
          />
          <ChannelScheduleViewType
            id="channel_schedule_view"
            name="channel_schedule_view"
            label="View"
            defaultValue={selectedView}
            disabled={!selectedChannel?.id}
            onChange={(e) => {
              setLocalFilterValue(
                FILTERS_KEY,
                { channel: selectedChannel, view: e.target.value } || {}
              );
              handleViewChange(e);
            }}
          />
        </Box>
      </Stack>

      {!options.loading && !selectedChannel?.id && (
        <Table>
          <TableRow>
            <TableCell align="center">
              <Typography variant="body2" sx={{ my: 20 }}>
                Select channel to preview channel content
              </Typography>
            </TableCell>
          </TableRow>
        </Table>
      )}

      {selectedChannel?.id && (
        <Card>
          {options.totalRows > 0 && (
            <TablePagination
              component="div"
              count={options.totalRows}
              page={options.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={options.rowsPerPage}
              onRowsPerPageChange={(e) => {
                handleRowsPerPageChange(Number(e.target.value));
              }}
            />
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {options.columns.map((c, colIndex) => {
                    const stickyStyle = getStickyColumnStyle(colIndex, c.id);

                    return (
                      <TableCell
                        key={`col-${c.id}`}
                        align={c.align}
                        sx={{
                          cursor: c.sortable ? 'pointer' : 'inherit',
                          padding: '16px',
                          fontSize: '0.875rem !important',
                          ...stickyStyle,
                        }}
                        onClick={() => {
                          if (c.sortable) {
                            if (c.dataKey === options.sortBy) {
                              if (options.sortOrder === '') {
                                handleSortingChange(c.dataKey, 'asc');
                              } else if (options.sortOrder === 'asc') {
                                handleSortingChange(c.dataKey, 'desc');
                              } else if (options.sortOrder === 'desc') {
                                handleSortingChange('', '');
                              }
                            } else {
                              handleSortingChange(c.dataKey, 'asc');
                            }
                          }
                        }}
                      >
                        <Stack direction="row" justifyContent={c.align}>
                          {c.sortable && (
                            <Sortable
                              dataKey={c.dataKey}
                              sortBy={options.sortBy}
                              sortOrder={options.sortOrder}
                            />
                          )}
                          {c.label}
                        </Stack>
                      </TableCell>
                    );
                  })}
                  {sundayDates.map((c) => (
                    <TableCell
                      key={`col-${c.date}`}
                      align="center"
                      width="10%"
                      sx={{
                        cursor: 'inherit',
                        padding: '16px',
                        fontSize: '0.875rem !important',
                        minWidth: '100px',
                      }}
                    >
                      <Stack direction="row" justifyContent="center">
                        {c.quarter} W{c.week}
                        <br />
                        {c.date}
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {!options.loading && !options.error && (
                  <>
                    {options.rows.map((r, index) => (
                      <>
                        <TableRow
                          key={`row-${r[options.columns[0].dataKey]}-${index}`}
                        >
                          {options.columns.map((c, colIndex) => {
                            const stickyStyle = getStickyColumnStyle(
                              colIndex,
                              c.id
                            );

                            return (
                              <TableCell
                                key={`data-col-${c.id}-${r[c.dataKey]}`}
                                align={c.align}
                                width={c.width}
                                sx={{
                                  padding: '16px',
                                  fontSize: '0.875rem !important',
                                  ...stickyStyle,
                                }}
                                variant="string"
                              >
                                {c.id === 'title' &&
                                selectedView !== 'MOVIES' ? (
                                  (() => {
                                    const today = new Date();
                                    let totalCount = 0;
                                    let scheduledCount = 0;
                                    let liveCount = 0;

                                    if (
                                      selectedView === 'COLLECTIONS' ||
                                      (selectedView === 'APP_VIEW' &&
                                        r.level === 1)
                                    ) {
                                      totalCount = r?.subRows?.length || 0;

                                      scheduledCount =
                                        r?.subRows?.filter(
                                          (sr) =>
                                            sr.dateStart &&
                                            new Date(sr.dateStart) > today
                                        ).length || 0;

                                      liveCount =
                                        r?.subRows?.filter((sr) => {
                                          const start = sr.dateStart
                                            ? new Date(sr.dateStart)
                                            : null;
                                          const end = sr.dateEnd
                                            ? new Date(sr.dateEnd)
                                            : null;
                                          return (
                                            start &&
                                            start <= today &&
                                            (!end || end >= today)
                                          );
                                        }).length || 0;
                                    } else if (
                                      selectedView === 'SERIES' ||
                                      (selectedView === 'APP_VIEW' &&
                                        r.level === 2)
                                    ) {
                                      (r?.subRows || []).forEach((season) => {
                                        (season?.subRows || []).forEach(
                                          (episode) => {
                                            const start = episode.dateStart
                                              ? new Date(episode.dateStart)
                                              : null;
                                            const end = episode.dateEnd
                                              ? new Date(episode.dateEnd)
                                              : null;

                                            totalCount += 1;
                                            if (start) {
                                              if (start > today) {
                                                scheduledCount += 1;
                                              } else if (
                                                start <= today &&
                                                (!end || end >= today)
                                              ) {
                                                liveCount += 1;
                                              }
                                            }
                                          }
                                        );
                                      });
                                    }

                                    return (
                                      <>
                                        <Typography
                                          variant="body2"
                                          component="span"
                                          color="inherit"
                                        >
                                          {r[c.dataKey] || ''}
                                        </Typography>

                                        <Button
                                          variant="outlined"
                                          size="small"
                                          color="secondary"
                                          sx={{
                                            px: 1,
                                            margin: 0,
                                            minWidth: 'auto',
                                            mx: 0.5,
                                            cursor: 'default',
                                          }}
                                        >
                                          TOT: {totalCount}
                                        </Button>

                                        <Button
                                          variant="outlined"
                                          size="small"
                                          color="secondary"
                                          sx={{
                                            px: 1,
                                            margin: 0,
                                            minWidth: 'auto',
                                            mx: 0.5,
                                            cursor: 'default',
                                          }}
                                        >
                                          SCH: {scheduledCount}
                                        </Button>

                                        <Button
                                          variant="outlined"
                                          size="small"
                                          color="secondary"
                                          sx={{
                                            px: 1,
                                            margin: 0,
                                            minWidth: 'auto',
                                            mx: 0.5,
                                            cursor: 'default',
                                          }}
                                        >
                                          LIVE: {liveCount}
                                        </Button>
                                      </>
                                    );
                                  })()
                                ) : (
                                  <CellValue
                                    type={c.type}
                                    dataKey={c.dataKey}
                                    value={r[c.dataKey]}
                                    rowData={r}
                                    expandSubRow={r.isExpanded}
                                    handleShowSubRow={() => handleShowSubRow(r)}
                                  />
                                )}
                              </TableCell>
                            );
                          })}
                          {sundayDates.map((c) => (
                            <TableCell
                              key={`col-${c.date}`}
                              align="center"
                              width="10%"
                              sx={{
                                cursor: 'inherit',
                                padding: '16px',
                                fontSize: '0.875rem !important',
                                backgroundColor:
                                  r.dateStart &&
                                  new Date(r.dateStart) < new Date(c.date) &&
                                  (r.dateEnd
                                    ? new Date(r.dateEnd) > new Date(c.date)
                                    : 1)
                                    ? 'gray'
                                    : '',
                              }}
                            />
                          ))}
                        </TableRow>
                        {r.isExpanded &&
                          r?.subRows?.length > 0 &&
                          r.subRows.map((sr) => (
                            <>
                              <TableRow
                                sx={{
                                  backgroundColor: '#ebebeb',
                                  boxShadow: 'inset 0 0 2px rgba(0, 0, 0, 0.2)',
                                }}
                              >
                                {options.columns.map((src, cIndex) => {
                                  const stickyStyle = getStickyColumnStyle(
                                    cIndex,
                                    src.id
                                  );
                                  return src.type ===
                                    CELL_DATA_TYPES.SUB_LIST &&
                                    (selectedView === 'COLLECTIONS' ||
                                      (selectedView === 'APP_VIEW' &&
                                        r.level === 1)) ? (
                                    <TableCell
                                      key={`data-col-${sr.id}-${src.id}`}
                                      align={src.align}
                                      width={src.width}
                                      sx={{
                                        padding: '16px',
                                        fontSize: '0.875rem !important',
                                        ...stickyStyle,
                                      }}
                                      variant="string"
                                    />
                                  ) : (
                                    <TableCell
                                      key={`data-col-${sr.id}-${src.id}`}
                                      align={src.align}
                                      width={src.width}
                                      sx={{
                                        padding: '16px',
                                        fontSize: '0.875rem !important',
                                        ...stickyStyle,
                                      }}
                                      variant="string"
                                    >
                                      <Collapse in timeout="auto" unmountOnExit>
                                        {src.id === 'title' &&
                                        selectedView !== 'MOVIES' &&
                                        selectedView !== 'COLLECTIONS' &&
                                        !(
                                          selectedView === 'APP_VIEW' &&
                                          r.level === 1
                                        ) ? (
                                          (() => {
                                            const today = new Date();
                                            let totalCount = 0;
                                            let scheduledCount = 0;
                                            let liveCount = 0;
                                            if (
                                              selectedView === 'SERIES' ||
                                              (selectedView === 'APP_VIEW' &&
                                                r.level === 2)
                                            ) {
                                              totalCount =
                                                sr?.subRows?.length || 0;

                                              scheduledCount =
                                                sr?.subRows?.filter(
                                                  (row) =>
                                                    row.dateStart &&
                                                    new Date(row.dateStart) >
                                                      today
                                                ).length || 0;

                                              liveCount =
                                                sr?.subRows?.filter((row) => {
                                                  const start = row.dateStart
                                                    ? new Date(row.dateStart)
                                                    : null;
                                                  const end = row.dateEnd
                                                    ? new Date(row.dateEnd)
                                                    : null;
                                                  return (
                                                    start &&
                                                    start <= today &&
                                                    (!end || end >= today)
                                                  );
                                                }).length || 0;
                                            }

                                            return (
                                              <>
                                                <Typography
                                                  variant="body2"
                                                  component="span"
                                                  color="inherit"
                                                >
                                                  {sr[src.dataKey] || ''}
                                                </Typography>

                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  color="secondary"
                                                  sx={{
                                                    px: 1,
                                                    margin: 0,
                                                    minWidth: 'auto',
                                                    mx: 0.5,
                                                    cursor: 'default',
                                                  }}
                                                >
                                                  TOT: {totalCount}
                                                </Button>

                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  color="secondary"
                                                  sx={{
                                                    px: 1,
                                                    margin: 0,
                                                    minWidth: 'auto',
                                                    mx: 0.5,
                                                    cursor: 'default',
                                                  }}
                                                >
                                                  SCH: {scheduledCount}
                                                </Button>

                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  color="secondary"
                                                  sx={{
                                                    px: 1,
                                                    margin: 0,
                                                    minWidth: 'auto',
                                                    mx: 0.5,
                                                    cursor: 'default',
                                                  }}
                                                >
                                                  LIVE: {liveCount}
                                                </Button>
                                              </>
                                            );
                                          })()
                                        ) : (
                                          <CellValue
                                            type={src.type}
                                            dataKey={src.dataKey}
                                            value={sr[src.dataKey]}
                                            rowData={sr}
                                            expandSubRow={sr.isExpanded}
                                            handleShowSubRow={() =>
                                              handleShowSubSubRow(r, sr)
                                            }
                                          />
                                        )}
                                      </Collapse>
                                    </TableCell>
                                  );
                                })}
                                {sundayDates.map((c) => (
                                  <TableCell
                                    key={`col-${c.date}`}
                                    align="center"
                                    width="10%"
                                    sx={{
                                      cursor: 'inherit',
                                      padding: '16px',
                                      fontSize: '0.875rem !important',
                                      backgroundColor:
                                        sr.dateStart &&
                                        new Date(sr.dateStart) <
                                          new Date(c.date) &&
                                        (sr.dateEnd
                                          ? new Date(sr.dateEnd) >
                                            new Date(c.date)
                                          : 1)
                                          ? 'gray'
                                          : '',
                                    }}
                                  />
                                ))}
                              </TableRow>
                              {sr.isExpanded &&
                                sr?.subRows?.length > 0 &&
                                sr.subRows.map((ssr) => (
                                  <TableRow
                                    sx={{
                                      backgroundColor: '#ebebeb',
                                      boxShadow:
                                        'inset 0 0 2px rgba(0, 0, 0, 0.2)',
                                    }}
                                  >
                                    {options.columns.map((src, ind) => {
                                      const stickyStyle = getStickyColumnStyle(
                                        ind,
                                        src.id
                                      );
                                      return src.type ===
                                        CELL_DATA_TYPES.SUB_LIST ? (
                                        <TableCell
                                          key={`data-col-${ssr.id}-${src.id}`}
                                          align={src.align}
                                          width={src.width}
                                          sx={{
                                            padding: '16px',
                                            fontSize: '0.875rem !important',
                                            ...stickyStyle,
                                          }}
                                          variant="string"
                                        />
                                      ) : (
                                        <TableCell
                                          key={`data-col-${ssr.id}-${src.id}`}
                                          align={src.align}
                                          width={src.width}
                                          sx={{
                                            padding: '16px',
                                            fontSize: '0.875rem !important',
                                            ...stickyStyle,
                                          }}
                                          variant="string"
                                        >
                                          <Collapse
                                            in
                                            timeout="auto"
                                            unmountOnExit
                                          >
                                            <CellValue
                                              type={src.type}
                                              dataKey={src.dataKey}
                                              value={ssr[src.dataKey]}
                                            />
                                          </Collapse>
                                        </TableCell>
                                      );
                                    })}
                                    {sundayDates.map((c) => (
                                      <TableCell
                                        key={`col-${c.date}`}
                                        align="center"
                                        width="10%"
                                        sx={{
                                          cursor: 'inherit',
                                          padding: '16px',
                                          fontSize: '0.875rem !important',
                                          backgroundColor:
                                            ssr.dateStart &&
                                            new Date(ssr.dateStart) <
                                              new Date(c.date) &&
                                            (ssr.dateEnd
                                              ? new Date(ssr.dateEnd) >
                                                new Date(c.date)
                                              : 1)
                                              ? 'gray'
                                              : '',
                                        }}
                                      />
                                    ))}
                                  </TableRow>
                                ))}
                            </>
                          ))}
                      </>
                    ))}
                  </>
                )}

                {!options.loading &&
                  !options.error &&
                  options.totalRows === 0 && (
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={options.columns.length + sundayDates.length}
                      >
                        <NoData />
                      </TableCell>
                    </TableRow>
                  )}

                {!options.loading &&
                  options.error &&
                  options.totalRows === 0 && (
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={options.columns.length + sundayDates.length}
                      >
                        <Error />
                      </TableCell>
                    </TableRow>
                  )}

                {options.loading && (
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={options.columns.length + sundayDates.length}
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {options.totalRows > 0 && (
            <TablePagination
              component="div"
              count={options.totalRows}
              page={options.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={options.rowsPerPage}
              onRowsPerPageChange={(e) => {
                handleRowsPerPageChange(Number(e.target.value));
              }}
            />
          )}
        </Card>
      )}
    </Box>
  );
};

export default ChannelPreview;
